import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@learn-me/ui";
import {
  Volume2,
  Trash2,
  Loader2,
  Play,
  RefreshCw,
  Headphones,
} from "lucide-react";
import { adminApi } from "../api/adminApi";
import toast from "react-hot-toast";

interface ChapterAudioToolsProps {
  chapterId: string;
  hasContent: boolean;
  audioUrl?: string | null;
  audioDuration?: number | null;
  audioVoice?: string | null;
  onAudioGenerated: () => void;
}

export function ChapterAudioTools({
  chapterId,
  hasContent,
  audioUrl,
  audioDuration,
  audioVoice,
  onAudioGenerated,
}: ChapterAudioToolsProps) {
  const [selectedVoice, setSelectedVoice] = useState(
    audioVoice || "es-MX-DaliaNeural",
  );
  const queryClient = useQueryClient();

  const { data: voices = [] } = useQuery({
    queryKey: ["tts-voices"],
    queryFn: () => adminApi.getTTSVoices(),
    staleTime: Infinity,
  });

  const generateMutation = useMutation({
    mutationFn: () =>
      adminApi.generateChapterAudio(chapterId, { voice: selectedVoice }),
    onSuccess: () => {
      toast.success("Audio generado exitosamente");
      queryClient.invalidateQueries({ queryKey: ["chapter", chapterId] });
      onAudioGenerated();
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Error al generar audio",
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => adminApi.deleteChapterAudio(chapterId),
    onSuccess: () => {
      toast.success("Audio eliminado");
      queryClient.invalidateQueries({ queryKey: ["chapter", chapterId] });
      onAudioGenerated();
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Error al eliminar audio",
      );
    },
  });

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const hasAudio = !!audioUrl;

  return (
    <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-indigo-800 text-base">
          <Headphones className="h-5 w-5" />
          Audio TTS
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {hasAudio ? (
          <>
            {/* Audio info */}
            <div className="p-3 bg-white/60 rounded-lg border border-indigo-200">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-indigo-100 rounded-lg">
                    <Volume2 className="h-4 w-4 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-indigo-800">
                      Audio generado
                    </p>
                    <p className="text-xs text-indigo-600">
                      Voz: {audioVoice || "Desconocida"} |{" "}
                      {audioDuration ? formatDuration(audioDuration) : "N/A"}
                    </p>
                  </div>
                </div>
                <a
                  href={audioUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-100 rounded-lg transition-colors"
                  title="Reproducir audio"
                >
                  <Play className="h-4 w-4" />
                </a>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="flex-1 border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                onClick={() => generateMutation.mutate()}
                disabled={
                  generateMutation.isPending || deleteMutation.isPending
                }
              >
                {generateMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Regenerar
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-red-300 text-red-600 hover:bg-red-50"
                onClick={() => deleteMutation.mutate()}
                disabled={
                  deleteMutation.isPending || generateMutation.isPending
                }
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-indigo-700">
              Genera una version de audio del contenido para que los estudiantes
              puedan escuchar el capitulo.
            </p>

            {/* Voice selector */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-indigo-800">
                Seleccionar voz
              </label>
              <select
                value={selectedVoice}
                onChange={(e) => setSelectedVoice(e.target.value)}
                className="w-full p-2.5 border border-indigo-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                disabled={generateMutation.isPending}
              >
                {voices.map((voice) => (
                  <option key={voice.id} value={voice.id}>
                    {voice.name} - {voice.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Generate button */}
            <Button
              type="button"
              variant="outline"
              className="w-full border-indigo-300 text-indigo-700 hover:bg-indigo-100"
              onClick={() => generateMutation.mutate()}
              disabled={!hasContent || generateMutation.isPending}
            >
              {generateMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generando audio...
                </>
              ) : (
                <>
                  <Volume2 className="h-4 w-4 mr-2" />
                  Generar Audio
                </>
              )}
            </Button>

            {!hasContent && (
              <p className="text-xs text-indigo-500 text-center">
                El capitulo necesita contenido primero
              </p>
            )}
          </>
        )}

        {/* Cost info */}
        <p className="text-xs text-indigo-600 pt-2 border-t border-indigo-200">
          Costo: ~$0.015 por 1000 caracteres
        </p>
      </CardContent>
    </Card>
  );
}
